import { useCallback } from "react"
import { getTokenName, SupportedTokenName } from "@mezo/sdk"
import { Address } from "viem"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { bridge, ETHEREUM_CHAIN_ID } from "#/shared/constants"
import MezoBridge from "#/shared/constants/artifacts/MezoBridge"
import { BridgeableTokens } from "#/shared/types"

import useWriteContractAndWaitForTransaction from "../contract"

import useBridgingHistory from "./useBridgingHistory"

export default function useBridgeERC20(
  token: BridgeableTokens,
  amount?: bigint,
  recipient?: Address,
) {
  const { writeContractAsync, ...rest } =
    useWriteContractAndWaitForTransaction(ETHEREUM_CHAIN_ID)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token as SupportedTokenName),
  )

  const { setLatestBridgeTransaction } = useBridgingHistory()

  const bridgeErc20 = useCallback(
    async (options?: {
      onError?: (error: unknown) => void
      onSuccess?: () => void
    }) => {
      if (token === "BTC") {
        throw new Error("Can't bridge native BTC")
      }

      if (!recipient) {
        throw new Error("Recipient not defined")
      }

      if (!amount) {
        throw new Error("Amount not defined")
      }

      const { sourceToken } = bridge.BRIDGEABLE_TOKENS[token]

      if (token === "tBTC") {
        await writeContractAsync(
          {
            abi: MezoBridge.abi,
            address: MezoBridge.address,
            functionName: "bridgeTBTC",
            args: [amount, recipient],
            chainId: ETHEREUM_CHAIN_ID,
          },
          options,
        )
        return
      }

      const transactionHash = await writeContractAsync(
        {
          abi: MezoBridge.abi,
          address: MezoBridge.address,
          functionName: "bridgeERC20",
          args: [sourceToken, amount, recipient],
          chainId: ETHEREUM_CHAIN_ID,
        },
        options,
      )

      setLatestBridgeTransaction(
        amount,
        getTokenName(sourceToken)!,
        tokenDecimals,
        transactionHash,
      )
    },
    [
      writeContractAsync,
      token,
      amount,
      recipient,
      setLatestBridgeTransaction,
      tokenDecimals,
    ],
  )

  return {
    ...rest,
    bridgeErc20,
  }
}
